import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, Apple, Monitor, Smartphone, Chrome, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Always-visible install button. Behaviour:
 * - If browser supports beforeinstallprompt (Chrome/Edge on Win/Mac/Android) — one-click native install.
 * - Otherwise open a lightweight modal with OS/browser-specific instructions.
 */
function detectPlatform() {
  if (typeof navigator === "undefined") return { os: "other", browser: "other" };
  const ua = navigator.userAgent;
  const platform = navigator.platform || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isMac = !isIOS && /Mac/i.test(platform);
  const isWin = /Win/i.test(platform);
  const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
  const isEdge = /Edg\//i.test(ua);
  const isChrome = /Chrome\//i.test(ua) && !isEdge;
  const isFirefox = /Firefox\//i.test(ua);
  let os = "other";
  if (isIOS) os = "ios";
  else if (isAndroid) os = "android";
  else if (isMac) os = "mac";
  else if (isWin) os = "windows";
  let browser = "other";
  if (isEdge) browser = "edge";
  else if (isChrome) browser = "chrome";
  else if (isSafari) browser = "safari";
  else if (isFirefox) browser = "firefox";
  return { os, browser };
}

export default function InstallPWAButton() {
  const { lang } = useLang();
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState({ os: "other", browser: "other" });

  useEffect(() => {
    setPlatform(detectPlatform());
    const standalone =
      window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    if (standalone) setInstalled(true);
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); setOpen(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onClick = async () => {
    if (prompt) {
      try {
        await prompt.prompt();
        await prompt.userChoice;
        setPrompt(null);
      } catch { /* user dismissed */ }
      return;
    }
    setOpen(true);
  };

  if (installed) return null;

  const label = lang === "vi" ? "Cài đặt" : "Install";

  return (
    <>
      <button
        data-testid="install-pwa-btn"
        onClick={onClick}
        className="h-10 px-3.5 rounded-full border border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10 transition-colors flex items-center gap-2 text-sm"
        title={label}
      >
        <Download size={15} />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && <InstallInstructionsModal platform={platform} onClose={() => setOpen(false)} lang={lang} />}
    </>
  );
}

function InstallInstructionsModal({ platform, onClose, lang }) {
  const { os, browser } = platform;

  const guides = getGuides(lang);
  const primary = getPrimary(os, browser, guides);
  const alt = getAlt(os, browser, guides).filter((g) => g.key !== primary.key);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Portal into document.body so `position:fixed` is anchored to the viewport
  // (an ancestor `transform` in the app tree would otherwise break centering).
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm py-10 px-4"
      onClick={onClose}
    >
      <div
        data-testid="install-modal"
        className="max-w-lg w-full rounded-3xl border border-border bg-card shadow-2xl overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-1">
              {lang === "vi" ? "Cài đặt Readlet" : "Install Readlet"}
            </p>
            <h3 className="font-display text-2xl leading-tight">
              {lang === "vi" ? "Sử dụng như một ứng dụng" : "Use it like an app"}
            </h3>
          </div>
          <button
            data-testid="install-modal-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-accent grid place-items-center"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {lang === "vi"
              ? "Readlet là một ứng dụng web tiến bộ (PWA). Sau khi cài đặt, nó chạy trong cửa sổ riêng như một ứng dụng thật — trên Mac, Windows, Android hoặc iPhone."
              : "Readlet is a Progressive Web App. Once installed, it runs in its own window like a native app — on Mac, Windows, Android or iPhone."}
          </p>

          <Guide guide={primary} highlight lang={lang} />

          {alt.length > 0 && (
            <details className="rounded-2xl border border-border">
              <summary className="cursor-pointer px-4 py-3 text-sm text-muted-foreground select-none">
                {lang === "vi" ? "Hướng dẫn cho hệ điều hành khác" : "Other operating systems"}
              </summary>
              <div className="px-4 pb-4 space-y-3">
                {alt.map((g) => <Guide key={g.key} guide={g} lang={lang} />)}
              </div>
            </details>
          )}

          <p className="text-xs text-muted-foreground pt-2">
            {lang === "vi"
              ? "Lưu ý: Đây là ứng dụng web (PWA), không phải file .exe / .dmg. Vẫn hoạt động ngoại tuyến và trên cả điện thoại."
              : "Note: This is a web app (PWA), not a .exe / .dmg installer. It still works offline and on your phone."}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Guide({ guide, highlight = false, lang }) {
  const Icon = guide.icon;
  return (
    <div
      className={`rounded-2xl p-5 ${highlight ? "border border-primary/40 bg-primary/[0.04]" : "border border-border bg-background"}`}
      data-testid={`install-guide-${guide.key}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Icon size={16} />
        </div>
        <p className="font-display text-lg leading-tight">{guide.title}</p>
      </div>
      <ol className="space-y-2 text-sm leading-relaxed">
        {guide.steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-primary font-semibold tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <span dangerouslySetInnerHTML={{ __html: s }} />
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---- guides ----
function getGuides(lang) {
  const en = {
    mac_chrome: {
      key: "mac_chrome", icon: Chrome,
      title: "Mac · Chrome / Edge",
      steps: [
        "Click the <b>install icon</b> in the right of the address bar (a small screen with a down arrow).",
        "Or open the <b>⋮ menu → Cast, save, and share → Install Readlet</b>.",
        "Readlet opens in its own window and lands in your Applications folder.",
      ],
    },
    mac_safari: {
      key: "mac_safari", icon: Apple,
      title: "Mac · Safari (macOS Sonoma+)",
      steps: [
        "In Safari, open the <b>File</b> menu.",
        "Choose <b>Add to Dock…</b>",
        "Confirm the name and click <b>Add</b>. Readlet now lives in your Dock.",
      ],
    },
    windows_chrome: {
      key: "windows_chrome", icon: Monitor,
      title: "Windows · Chrome / Edge",
      steps: [
        "Click the <b>install icon</b> at the right of the address bar (or press <b>Ctrl + Shift + A</b>).",
        "In Edge: <b>⋯ menu → Apps → Install this site as an app</b>.",
        "Pin the new Readlet window to your taskbar.",
      ],
    },
    android_chrome: {
      key: "android_chrome", icon: Smartphone,
      title: "Android · Chrome",
      steps: [
        "Tap the <b>⋮ menu</b> in the top right.",
        "Choose <b>Install app</b> (or <b>Add to Home screen</b>).",
        "Readlet appears on your home screen like a native app.",
      ],
    },
    ios_safari: {
      key: "ios_safari", icon: Smartphone,
      title: "iPhone / iPad · Safari",
      steps: [
        "Tap the <b>Share</b> icon (a square with an arrow up).",
        "Scroll down and tap <b>Add to Home Screen</b>.",
        "Tap <b>Add</b>. Readlet appears on your home screen.",
      ],
    },
  };
  const vi = {
    mac_chrome: {
      key: "mac_chrome", icon: Chrome,
      title: "Mac · Chrome / Edge",
      steps: [
        "Bấm vào <b>biểu tượng cài đặt</b> ở bên phải thanh địa chỉ.",
        "Hoặc mở <b>menu ⋮ → Cast, save, and share → Install Readlet</b>.",
        "Readlet mở trong cửa sổ riêng và nằm trong thư mục Applications.",
      ],
    },
    mac_safari: {
      key: "mac_safari", icon: Apple,
      title: "Mac · Safari (macOS Sonoma trở lên)",
      steps: [
        "Trong Safari, mở menu <b>File</b>.",
        "Chọn <b>Add to Dock…</b>",
        "Xác nhận tên và bấm <b>Add</b>. Readlet nằm trong Dock.",
      ],
    },
    windows_chrome: {
      key: "windows_chrome", icon: Monitor,
      title: "Windows · Chrome / Edge",
      steps: [
        "Bấm vào <b>biểu tượng cài đặt</b> ở thanh địa chỉ (hoặc <b>Ctrl + Shift + A</b>).",
        "Với Edge: <b>⋯ → Apps → Install this site as an app</b>.",
        "Ghim cửa sổ Readlet vào thanh tác vụ.",
      ],
    },
    android_chrome: {
      key: "android_chrome", icon: Smartphone,
      title: "Android · Chrome",
      steps: [
        "Chạm menu <b>⋮</b> ở góc trên bên phải.",
        "Chọn <b>Install app</b> hoặc <b>Add to Home screen</b>.",
        "Readlet xuất hiện trên màn hình chính như ứng dụng thật.",
      ],
    },
    ios_safari: {
      key: "ios_safari", icon: Smartphone,
      title: "iPhone / iPad · Safari",
      steps: [
        "Chạm biểu tượng <b>Share</b> (hình vuông có mũi tên).",
        "Cuộn xuống và chạm <b>Add to Home Screen</b>.",
        "Chạm <b>Add</b>. Readlet lên màn hình chính.",
      ],
    },
  };
  return lang === "vi" ? vi : en;
}

function getPrimary(os, browser, g) {
  if (os === "mac" && browser === "safari") return g.mac_safari;
  if (os === "mac") return g.mac_chrome;
  if (os === "windows") return g.windows_chrome;
  if (os === "android") return g.android_chrome;
  if (os === "ios") return g.ios_safari;
  return g.mac_chrome;
}
function getAlt(_os, _browser, g) {
  return [g.mac_chrome, g.mac_safari, g.windows_chrome, g.android_chrome, g.ios_safari];
}
